import React from 'react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Shield, Star, Flame, AlertTriangle, User } from 'lucide-react';

/**
 * ReliabilityBadge - Displays talent reliability/commitment badges
 * 
 * Badge Levels:
 * - top_talent (95-100%): Green, shows "Top Talent"
 * - reliable (85-94%): Blue, shows "Reliable"
 * - needs_improvement (70-84%): Yellow, shows "Needs Improvement"
 * - at_risk (<70%): Red, shows "At Risk"
 * - new_talent: Gray, shows "New Talent"
 * 
 * Commitment Badges:
 * - high_commitment: Green fire, shows "High Commitment"
 * - standard: Blue, shows "Standard"
 * - low_commitment: Yellow, shows "Low Commitment"
 */

const BADGE_CONFIG = {
  top_talent: {
    label: 'Top Talent',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: Star,
    iconColor: 'text-green-600'
  },
  reliable: {
    label: 'Reliable',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Shield,
    iconColor: 'text-blue-600'
  },
  needs_improvement: {
    label: 'Developing',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: AlertTriangle,
    iconColor: 'text-yellow-600'
  },
  at_risk: {
    label: 'At Risk',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-600'
  },
  new_talent: {
    label: 'New',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: User,
    iconColor: 'text-gray-500'
  },
  high_commitment: {
    label: 'High Commitment',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: Flame,
    iconColor: 'text-orange-500'
  },
  standard: {
    label: 'Standard',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Shield,
    iconColor: 'text-blue-600'
  },
  low_commitment: {
    label: 'Low Commitment',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: AlertTriangle,
    iconColor: 'text-yellow-600'
  }
};

export const ReliabilityBadge = ({ 
  badge, 
  score, 
  showScore = false,
  size = 'sm',
  showTooltip = true,
  tooltipContent,
  className = ''
}) => {
  const config = BADGE_CONFIG[badge] || BADGE_CONFIG.new_talent;
  const Icon = config.icon;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5'
  };
  
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-4 h-4'
  };
  
  const badgeElement = (
    <Badge 
      variant="outline" 
      className={`${config.color} ${sizeClasses[size]} font-medium ${className}`}
      data-testid={`reliability-badge-${badge}`}
    >
      <Icon className={`${iconSizes[size]} ${config.iconColor} mr-1`} />
      {config.label}
      {showScore && score > 0 && (
        <span className="ml-1 opacity-75">({score.toFixed(0)}%)</span>
      )}
    </Badge>
  );
  
  if (!showTooltip) return badgeElement;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeElement}
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipContent || `${config.label}${score > 0 ? ` - ${score.toFixed(0)}% reliability` : ''}`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const CommitmentBadge = ({ 
  badge, 
  completed = 0,
  size = 'sm',
  showTooltip = true,
  className = ''
}) => {
  const config = BADGE_CONFIG[badge] || BADGE_CONFIG.new_talent;
  const Icon = config.icon;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5'
  };
  
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-4 h-4'
  };
  
  const badgeElement = (
    <Badge 
      variant="outline" 
      className={`${config.color} ${sizeClasses[size]} font-medium ${className}`}
      data-testid={`commitment-badge-${badge}`}
    >
      <Icon className={`${iconSizes[size]} ${config.iconColor} mr-1`} />
      {config.label}
    </Badge>
  );
  
  if (!showTooltip || badge === 'new_talent') return badgeElement;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeElement}
        </TooltipTrigger>
        <TooltipContent>
          <p>Completed {completed} multi-day assignments</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * WhyThisTalent - Displays reasons why a talent is recommended
 */
export const WhyThisTalent = ({ reasons = [], className = '' }) => {
  if (!reasons || reasons.length === 0) return null;
  
  return (
    <div className={`text-xs text-gray-600 space-y-0.5 ${className}`} data-testid="why-this-talent">
      {reasons.map((reason, idx) => (
        <div key={idx} className="flex items-center gap-1">
          <span className="text-cyan-500">•</span>
          <span>{reason}</span>
        </div>
      ))}
    </div>
  );
};

export default ReliabilityBadge;
